/**
 * Articles Page - Komplette Artikelverwaltung mit Material-UI
 * Optimiert für alle Geräte mit responsivem Design
 */

import React, { useState, useEffect } from "react";
import {
  Article,
  ArticleCategory,
  ArticleCategoryInfo,
  ArticleUnitInfo,
  UserRole,
} from "../types/api-types";
import { apiClient, ensureTokenIsSet } from "../services/apiClient";
import { useAuth } from "../context/AuthContext";
import LoadingSpinner from "../components/Common/LoadingSpinner";
import ConfirmDialog from "../components/Common/ConfirmDialog";

// Material-UI Imports
import {
  Container,
  Paper,
  Stack,
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  IconButton,
  Alert,
  Fab,
  Divider,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  useTheme,
  useMediaQuery,
} from "@mui/material";

// Material-UI Icons
import {
  Add as AddIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Inventory as InventoryIcon,
  Euro as EuroIcon,
  Scale as ScaleIcon,
  Tag as TagIcon,
  Notes as NotesIcon,
  Public as PublicIcon,
  CheckCircle as CheckCircleIcon,
  Settings as SettingsIcon,
} from "@mui/icons-material";

const ArticlesPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { user } = useAuth();
  const isAdmin = user?.role === UserRole.ADMIN;

  const [articles, setArticles] = useState<Article[]>([]);
  const [categories, setCategories] = useState<ArticleCategoryInfo[]>([]);
  const [units, setUnits] = useState<ArticleUnitInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<string>("");
  const [selectedArticle, setSelectedArticle] = useState<Article | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [articleToDelete, setArticleToDelete] = useState<Article | null>(null);

  // Load articles
  const loadArticles = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log("🔄 Starting to load articles...");

      // Stelle sicher, dass das Token gesetzt ist
      ensureTokenIsSet();

      // Check token
      const token = localStorage.getItem("accessToken");
      console.log("🔑 Auth token available:", token ? "YES" : "NO");

      console.log("📡 Making API call to getArticles...");
      const [articleList, categoryList, unitList] = await Promise.all([
        apiClient.getArticles(),
        apiClient.getArticleCategories(),
        apiClient.getArticleUnits(),
      ]);

      console.log("✅ Raw API response:", articleList);
      console.log("📋 Response type:", typeof articleList);
      console.log("📊 Is array?", Array.isArray(articleList));

      if (Array.isArray(articleList)) {
        console.log("📊 Number of articles received:", articleList.length);
        articleList.forEach((article, index) => {
          console.log(`Article ${index + 1}:`, {
            id: article.id,
            name: article.name,
            category: article.category,
            isActive: article.isActive,
          });
        });
      } else {
        console.log("⚠️ Response is not an array:", articleList);
      }

      setArticles(Array.isArray(articleList) ? articleList : []);
      setCategories(Array.isArray(categoryList) ? categoryList : []);
      setUnits(Array.isArray(unitList) ? unitList : []);
      console.log("✅ Articles set in state successfully");
    } catch (err: any) {
      console.error("❌ Error loading articles:", err);
      console.error("❌ Error details:", {
        message: err.message,
        status: err.status,
        statusText: err.statusText,
        stack: err.stack,
      });
      setError(err.message || "Fehler beim Laden der Artikel");
      setArticles([]); // Ensure articles is always an array
    } finally {
      setLoading(false);
      console.log("🏁 Loading articles finished");
    }
  };

  useEffect(() => {
    loadArticles();
  }, []);

  // Filter articles
  const filteredArticles = (articles || []).filter((article) => {
    // Exclude BUNDLE category
    if (article.category === ArticleCategory.BUNDLE) {
      return false;
    }
    const matchesSearch =
      article.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      article.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      article.sku?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory =
      !categoryFilter || article.category === categoryFilter;
    const result = matchesSearch && matchesCategory;

    // Debug logging
    if (articles.length > 0) {
      console.log(`🔍 Filtering article "${article.name}":`, {
        searchTerm,
        categoryFilter,
        articleCategory: article.category,
        matchesSearch,
        matchesCategory,
        finalResult: result,
      });
    }

    return result;
  });

  // Additional debug info
  console.log("📊 Filter Summary:", {
    totalArticles: articles.length,
    filteredArticles: filteredArticles.length,
    searchTerm,
    categoryFilter,
  });

  // Handle delete
  const handleDeleteArticle = async () => {
    if (!articleToDelete) return;

    try {
      await apiClient.deleteArticle(articleToDelete.id);
      setArticles(articles.filter((a) => a.id !== articleToDelete.id));
      setShowDeleteDialog(false);
      setArticleToDelete(null);
    } catch (err: any) {
      setError(err.message || "Fehler beim Löschen des Artikels");
    }
  };

  // Handle edit
  const handleEditArticle = (article: Article) => {
    setSelectedArticle(article);
    setShowForm(true);
  };

  // Handle add new
  const handleAddArticle = () => {
    setSelectedArticle(null);
    setShowForm(true);
  };

  // Category display names
  const getCategoryDisplayName = (category: string) => {
    const configured = categories.find((item) => item.key === category);
    if (configured) return configured.label;
    const names: Record<ArticleCategory, string> = {
      [ArticleCategory.TOOL]: "Werkzeuge",
      [ArticleCategory.LUBRICANT]: "Schmierstoffe",
      [ArticleCategory.CONSUMABLE]: "Verbrauchsmaterial",
      [ArticleCategory.SERVICE]: "Service",
      [ArticleCategory.BUNDLE]: "Bundle",
    };
    return names[category as ArticleCategory] || category;
  };

  // Get category color
  const getCategoryColor = (category: string) => {
    const colors: Record<
      ArticleCategory,
      "primary" | "secondary" | "error" | "info" | "success" | "warning"
    > = {
      [ArticleCategory.TOOL]: "primary",
      [ArticleCategory.LUBRICANT]: "warning",
      [ArticleCategory.CONSUMABLE]: "info",
      [ArticleCategory.SERVICE]: "success",
      [ArticleCategory.BUNDLE]: "secondary",
    };
    return colors[category as ArticleCategory] || "default";
  };

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: "EUR",
    }).format(amount);
  };

  // Clear filters
  const clearFilters = () => {
    setSearchTerm("");
    setCategoryFilter("");
  };

  if (loading) {
    return <LoadingSpinner text="Lade Artikel..." />;
  }

  if (error) {
    return (
      <Container
        maxWidth="lg"
        sx={{ py: { xs: 1, md: 2 }, px: { xs: 1, md: 3 } }}
      >
        <Paper
          sx={{
            p: { xs: 3, md: 4 },
            textAlign: "center",
            borderRadius: 2,
            background: "linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)",
          }}
        >
          <Typography
            variant={isMobile ? "h6" : "h5"}
            gutterBottom
            fontWeight="bold"
            color="error"
          >
            Fehler beim Laden der Artikel
          </Typography>
          <Typography variant="body1" color="error" gutterBottom>
            {error}
          </Typography>
          <Button
            variant="contained"
            color="error"
            size="large"
            startIcon={<InventoryIcon />}
            onClick={loadArticles}
            sx={{
              mt: 3,
              borderRadius: 2,
              px: 4,
              py: 1.5,
            }}
          >
            Erneut versuchen
          </Button>
        </Paper>
      </Container>
    );
  }

  if (showForm) {
    return (
      <ArticleForm
        article={selectedArticle}
        categories={categories}
        units={units}
        isAdmin={isAdmin}
        onCategoryCreated={async () => {
          const categoryList = await apiClient.getArticleCategories();
          setCategories(Array.isArray(categoryList) ? categoryList : []);
        }}
        onCategoryDeleted={async () => {
          const categoryList = await apiClient.getArticleCategories();
          setCategories(Array.isArray(categoryList) ? categoryList : []);
        }}
        onUnitCreated={async () => {
          const unitList = await apiClient.getArticleUnits();
          setUnits(Array.isArray(unitList) ? unitList : []);
        }}
        onUnitDeleted={async () => {
          const unitList = await apiClient.getArticleUnits();
          setUnits(Array.isArray(unitList) ? unitList : []);
        }}
        onSave={async (articleData) => {
          try {
            if (selectedArticle) {
              await apiClient.updateArticle(selectedArticle.id, articleData);
            } else {
              await apiClient.createArticle(articleData);
            }
            await loadArticles();
            setShowForm(false);
            setSelectedArticle(null);
          } catch (err: any) {
            setError(err.message || "Fehler beim Speichern des Artikels");
          }
        }}
        onCancel={() => {
          setShowForm(false);
          setSelectedArticle(null);
        }}
      />
    );
  }

  return (
    <Container
      maxWidth="xl"
      sx={{
        py: { xs: 1, md: 3 },
        px: { xs: 0.5, md: 3 },
        maxWidth: { xs: "100%", md: "xl" },
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 1.5, md: 3 },
          mb: 3,
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "white",
          borderRadius: 2,
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          spacing={2}
        >
          <Box>
            <Typography
              variant={isMobile ? "h5" : "h4"}
              component="h1"
              gutterBottom
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                fontWeight: "bold",
                mb: 1,
              }}
            >
              <InventoryIcon fontSize={isMobile ? "medium" : "large"} />
              Artikelverwaltung
            </Typography>
          </Box>

          {!isMobile && (
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                size="large"
                startIcon={<AddIcon />}
                onClick={handleAddArticle}
                sx={{
                  bgcolor: "rgba(255,255,255,0.2)",
                  color: "white",
                  "&:hover": {
                    bgcolor: "rgba(255,255,255,0.3)",
                  },
                  fontWeight: "bold",
                }}
              >
                Neuer Artikel
              </Button>
            </Stack>
          )}
        </Stack>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 3, borderRadius: 2 }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {/* Search and Filters */}
      <Paper
        sx={{
          p: { xs: 1.5, md: 3 },
          mb: 3,
          borderRadius: 2,
          mx: { xs: 0.5, md: 0 },
        }}
      >
        <Stack spacing={3}>
          <Typography
            variant="h6"
            sx={{ fontWeight: "bold", color: "primary.main" }}
          >
            Filter & Suche
          </Typography>

          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={5}>
              <TextField
                fullWidth
                label="Artikel durchsuchen"
                variant="outlined"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: searchTerm && (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setSearchTerm("")}
                        size="small"
                      >
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                placeholder="Artikelname, Beschreibung, SKU suchen..."
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Kategorie Filter</InputLabel>
                <Select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  label="Kategorie Filter"
                  sx={{
                    borderRadius: 2,
                  }}
                >
                  <MenuItem value="">Alle Kategorien</MenuItem>
                  {categories
                    .filter((category) => category.key !== ArticleCategory.BUNDLE)
                    .map((category) => (
                      <MenuItem key={category.key} value={category.key}>
                        {category.label}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                justifyContent="flex-end"
              >
                {(searchTerm || categoryFilter) && (
                  <Button
                    variant="outlined"
                    startIcon={<ClearIcon />}
                    onClick={clearFilters}
                    sx={{ borderRadius: 2 }}
                  >
                    Filter zurücksetzen
                  </Button>
                )}
                {isMobile && (
                  <Button
                    variant="contained"
                    fullWidth
                    startIcon={<AddIcon />}
                    onClick={handleAddArticle}
                    sx={{ borderRadius: 2 }}
                  >
                    Neuer Artikel
                  </Button>
                )}
              </Stack>
            </Grid>
          </Grid>
        </Stack>
      </Paper>

      {/* Articles Display */}
      {filteredArticles.length === 0 ? (
        <Paper
          sx={{
            p: { xs: 3, md: 6 },
            textAlign: "center",
            borderRadius: 2,
            background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
            mx: { xs: 0.5, md: 0 },
          }}
        >
          <InventoryIcon
            sx={{
              fontSize: { xs: 60, md: 80 },
              color: "text.secondary",
              mb: 2,
            }}
          />
          <Typography
            variant={isMobile ? "h6" : "h5"}
            gutterBottom
            fontWeight="bold"
          >
            Keine Artikel gefunden
          </Typography>
          <Typography variant="body1" color="text.secondary" gutterBottom>
            {searchTerm || categoryFilter
              ? "Passen Sie Ihre Suchkriterien an oder erstellen Sie einen neuen Artikel."
              : "Starten Sie mit Ihrem ersten Artikel und verwalten Sie alle Produkte professionell."}
          </Typography>
          {!searchTerm && !categoryFilter && (
            <Button
              variant="contained"
              size="large"
              startIcon={<AddIcon />}
              onClick={handleAddArticle}
              sx={{
                mt: 3,
                borderRadius: 2,
                px: 4,
                py: 1.5,
              }}
            >
              Ersten Artikel anlegen
            </Button>
          )}
        </Paper>
      ) : (
        <Grid
          container
          spacing={{ xs: 1, md: 3 }}
          sx={{ px: { xs: 0.5, md: 0 } }}
        >
          {filteredArticles.map((article) => (
            <Grid item xs={12} sm={6} md={6} lg={4} key={article.id}>
              <Card
                sx={{
                  height: { xs: "auto", md: "100%" },
                  maxHeight: { xs: 280, md: "none" },
                  display: "flex",
                  flexDirection: "column",
                  borderRadius: 2,
                  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: { xs: "none", md: "translateY(-4px)" },
                    boxShadow: "0 8px 30px rgba(0,0,0,0.15)",
                  },
                }}
              >
                <CardContent
                  sx={{
                    flexGrow: { xs: 0, md: 1 },
                    p: { xs: 1.5, md: 3 },
                    display: "flex",
                    flexDirection: "column",
                    minHeight: 0, // Wichtig für flex-shrink
                  }}
                >
                  {/* Header with Category Badge */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      mb: { xs: 1.5, md: 3 },
                      gap: 1,
                      flexShrink: 0,
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="h6"
                        component="h2"
                        gutterBottom
                        sx={{
                          fontWeight: "bold",
                          fontSize: { xs: "1rem", md: "1.25rem" },
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          lineHeight: 1.2,
                          mb: { xs: 0.5, md: 1 },
                        }}
                      >
                        {article.name}
                      </Typography>
                      {article.description && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            fontSize: { xs: "0.75rem", md: "0.875rem" },
                            lineHeight: 1.3,
                          }}
                        >
                          {article.description}
                        </Typography>
                      )}
                    </Box>
                    <Chip
                      label={getCategoryDisplayName(article.category)}
                      color={getCategoryColor(article.category)}
                      size="small"
                      sx={{
                        fontWeight: "bold",
                        borderRadius: 2,
                        flexShrink: 0,
                        fontSize: { xs: "0.65rem", md: "0.75rem" },
                        height: { xs: 22, md: 28 },
                        px: { xs: 0.5, md: 1 },
                      }}
                    />
                  </Box>

                  <Divider sx={{ my: { xs: 1, md: 2 }, flexShrink: 0 }} />

                  {/* Article Details - Scrollable on mobile */}
                  <Box
                    sx={{
                      flex: 1,
                      minHeight: 0,
                      overflow: { xs: "auto", md: "visible" },
                      "&::-webkit-scrollbar": {
                        display: "none",
                      },
                      scrollbarWidth: "none",
                    }}
                  >
                    <Stack spacing={{ xs: 0.5, md: 2 }}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <EuroIcon
                          sx={{
                            color: "success.main",
                            fontSize: { xs: 16, md: 20 },
                          }}
                        />
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: "bold",
                            fontSize: { xs: "1rem", md: "1.1rem" },
                          }}
                        >
                          {formatCurrency(article.price)}
                        </Typography>
                      </Box>

                      {article.unit && (
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <ScaleIcon
                            sx={{
                              color: "info.main",
                              fontSize: { xs: 16, md: 20 },
                            }}
                          />
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              fontSize: { xs: "0.8rem", md: "0.875rem" },
                            }}
                          >
                            Einheit: {article.unit}
                          </Typography>
                        </Box>
                      )}

                      {article.sku && (
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <TagIcon
                            sx={{
                              color: "warning.main",
                              fontSize: { xs: 16, md: 20 },
                            }}
                          />
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              fontSize: { xs: "0.8rem", md: "0.875rem" },
                              flex: 1,
                            }}
                          >
                            SKU: {article.sku}
                          </Typography>
                        </Box>
                      )}

                      {article.notes && (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 1,
                          }}
                        >
                          <NotesIcon
                            sx={{
                              color: "text.secondary",
                              fontSize: { xs: 16, md: 20 },
                              mt: 0.2,
                              flexShrink: 0,
                            }}
                          />
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              fontSize: { xs: "0.8rem", md: "0.875rem" },
                              flex: 1,
                            }}
                          >
                            {article.notes}
                          </Typography>
                        </Box>
                      )}

                      {article.isPublic && (
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <PublicIcon
                            sx={{
                              color: "primary.main",
                              fontSize: { xs: 16, md: 20 },
                            }}
                          />
                          <Typography
                            variant="body2"
                            color="primary.main"
                            sx={{
                              fontWeight: "bold",
                              fontSize: { xs: "0.8rem", md: "0.875rem" },
                            }}
                          >
                            Öffentlich sichtbar
                          </Typography>
                        </Box>
                      )}
                    </Stack>
                  </Box>
                </CardContent>

                <CardActions
                  sx={{
                    p: { xs: 1.5, md: 3 },
                    pt: { xs: 0.5, md: 0 },
                    justifyContent: "space-between",
                  }}
                >
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={
                      <EditIcon sx={{ fontSize: { xs: 14, md: 18 } }} />
                    }
                    onClick={() => handleEditArticle(article)}
                    sx={{
                      flex: 1,
                      mr: 1,
                      borderRadius: 2,
                      fontSize: { xs: "0.7rem", md: "0.875rem" },
                      py: { xs: 0.4, md: 0.75 },
                      minHeight: { xs: 32, md: 36 },
                      "&:hover": {
                        bgcolor: "primary.main",
                        color: "white",
                      },
                    }}
                  >
                    {isMobile ? "Edit" : "Bearbeiten"}
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    color="error"
                    startIcon={
                      <DeleteIcon sx={{ fontSize: { xs: 14, md: 18 } }} />
                    }
                    onClick={() => {
                      setArticleToDelete(article);
                      setShowDeleteDialog(true);
                    }}
                    sx={{
                      flex: 1,
                      ml: 1,
                      borderRadius: 2,
                      fontSize: { xs: "0.7rem", md: "0.875rem" },
                      py: { xs: 0.4, md: 0.75 },
                      minHeight: { xs: 32, md: 36 },
                      "&:hover": {
                        bgcolor: "error.main",
                        color: "white",
                      },
                    }}
                  >
                    {isMobile ? "Del" : "Löschen"}
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Floating Action Button for mobile */}
      <Fab
        color="primary"
        aria-label="add"
        sx={{
          position: "fixed",
          bottom: 16,
          right: 16,
          display: { xs: "flex", md: "none" },
        }}
        onClick={handleAddArticle}
      >
        <AddIcon />
      </Fab>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteDialog}
        title="Artikel löschen"
        message={`Möchten Sie den Artikel "${articleToDelete?.name}" wirklich löschen?`}
        confirmText="Löschen"
        cancelText="Abbrechen"
        type="danger"
        onConfirm={handleDeleteArticle}
        onCancel={() => {
          setShowDeleteDialog(false);
          setArticleToDelete(null);
        }}
      />
    </Container>
  );
};

interface ArticleTaxonomyDialogProps {
  open: boolean;
  categories: ArticleCategoryInfo[];
  units: ArticleUnitInfo[];
  onClose: () => void;
  onCategoryCreated: (category: ArticleCategoryInfo) => Promise<void>;
  onCategoryDeleted: (key: string) => Promise<void>;
  onUnitCreated: (unit: ArticleUnitInfo) => Promise<void>;
  onUnitDeleted: (unit: ArticleUnitInfo) => Promise<void>;
}

type DeleteTarget =
  | { type: "category"; key: string; label: string }
  | { type: "unit"; unit: ArticleUnitInfo };

const ArticleTaxonomyDialog: React.FC<ArticleTaxonomyDialogProps> = ({
  open,
  categories,
  units,
  onClose,
  onCategoryCreated,
  onCategoryDeleted,
  onUnitCreated,
  onUnitDeleted,
}) => {
  const [categoryLabel, setCategoryLabel] = useState("");
  const [unitLabel, setUnitLabel] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const activeCategories = categories.filter((category) => category.isActive);
  const activeUnits = units.filter((unit) => unit.isActive);
  const isProtectedUnit = (unit: ArticleUnitInfo) =>
    unit.key === "STUNDE" || unit.label.trim().toLowerCase() === "stunde";

  const handleCreateCategory = async () => {
    const label = categoryLabel.trim();
    if (!label) return;

    try {
      setSaving(true);
      setError(null);
      const created = await apiClient.createArticleCategory({ label });
      await onCategoryCreated(created);
      setCategoryLabel("");
    } catch (err: any) {
      setError(err?.message || "Kategorie konnte nicht angelegt werden.");
    } finally {
      setSaving(false);
    }
  };

  const handleCreateUnit = async () => {
    const label = unitLabel.trim();
    if (!label) return;

    try {
      setSaving(true);
      setError(null);
      const created = await apiClient.createArticleUnit({ label });
      await onUnitCreated(created);
      setUnitLabel("");
    } catch (err: any) {
      setError(err?.message || "Einheit konnte nicht angelegt werden.");
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;

    try {
      setSaving(true);
      setError(null);

      if (deleteTarget.type === "category") {
        await apiClient.deleteArticleCategory(deleteTarget.key);
        await onCategoryDeleted(deleteTarget.key);
      } else {
        await apiClient.deleteArticleUnit(deleteTarget.unit.key);
        await onUnitDeleted(deleteTarget.unit);
      }

      setDeleteTarget(null);
    } catch (err: any) {
      setError(
        err?.message ||
          "Der Eintrag wird verwendet und kann aktuell nicht gelöscht werden."
      );
      setDeleteTarget(null);
    } finally {
      setSaving(false);
    }
  };

  const renderMetaChip = (isSystem: boolean) =>
    isSystem ? <Chip size="small" label="Standard" variant="outlined" /> : null;

  return (
    <>
      <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth>
        <DialogTitle>Kategorien & Einheiten verwalten</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={3}>
            {error && (
              <Alert severity="warning" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Stack spacing={2}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    Kategorien
                  </Typography>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Neue Kategorie"
                      value={categoryLabel}
                      onChange={(event) => setCategoryLabel(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") {
                          event.preventDefault();
                          handleCreateCategory();
                        }
                      }}
                    />
                    <Button
                      type="button"
                      variant="contained"
                      startIcon={<AddIcon />}
                      disabled={saving || !categoryLabel.trim()}
                      onClick={handleCreateCategory}
                    >
                      Anlegen
                    </Button>
                  </Stack>
                  <List dense disablePadding>
                    {activeCategories.map((category) => (
                      <ListItem
                        key={category.key}
                        divider
                        secondaryAction={
                          <Tooltip title="Kategorie löschen">
                            <span>
                              <IconButton
                                edge="end"
                                color="error"
                                disabled={saving}
                                onClick={() =>
                                  setDeleteTarget({
                                    type: "category",
                                    key: category.key,
                                    label: category.label,
                                  })
                                }
                              >
                                <DeleteIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                        }
                      >
                        <ListItemText
                          primary={category.label}
                          primaryTypographyProps={{ fontWeight: 600 }}
                        />
                        {renderMetaChip(category.isSystem)}
                      </ListItem>
                    ))}
                  </List>
                </Stack>
              </Grid>

              <Grid item xs={12} md={6}>
                <Stack spacing={2}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    Einheiten
                  </Typography>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Neue Einheit"
                      value={unitLabel}
                      onChange={(event) => setUnitLabel(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") {
                          event.preventDefault();
                          handleCreateUnit();
                        }
                      }}
                    />
                    <Button
                      type="button"
                      variant="contained"
                      startIcon={<AddIcon />}
                      disabled={saving || !unitLabel.trim()}
                      onClick={handleCreateUnit}
                    >
                      Anlegen
                    </Button>
                  </Stack>
                  <List dense disablePadding>
                    {activeUnits.map((unit) => (
                      <ListItem
                        key={unit.key}
                        divider
                        secondaryAction={
                          <Tooltip
                            title={
                              isProtectedUnit(unit)
                                ? "Stunde wird für zeitbasierte Abrechnung benötigt"
                                : "Einheit löschen"
                            }
                          >
                            <span>
                              <IconButton
                                edge="end"
                                color="error"
                                disabled={saving || isProtectedUnit(unit)}
                                onClick={() =>
                                  setDeleteTarget({
                                    type: "unit",
                                    unit,
                                  })
                                }
                              >
                                <DeleteIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                        }
                      >
                        <ListItemText
                          primary={unit.label}
                          primaryTypographyProps={{ fontWeight: 600 }}
                        />
                        {renderMetaChip(unit.isSystem)}
                      </ListItem>
                    ))}
                  </List>
                </Stack>
              </Grid>
            </Grid>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button type="button" onClick={onClose} disabled={saving}>
            Schließen
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!deleteTarget}
        onClose={saving ? undefined : () => setDeleteTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Eintrag löschen</DialogTitle>
        <DialogContent dividers>
          <Typography>
            {deleteTarget?.type === "category"
              ? `Kategorie "${deleteTarget.label}" löschen?`
              : `Einheit "${deleteTarget?.unit.label}" löschen?`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            type="button"
            onClick={() => setDeleteTarget(null)}
            disabled={saving}
          >
            Abbrechen
          </Button>
          <Button
            type="button"
            color="error"
            variant="contained"
            onClick={handleConfirmDelete}
            disabled={saving}
          >
            Löschen
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

// Article Form Component
interface ArticleFormProps {
  article: Article | null;
  categories: ArticleCategoryInfo[];
  units: ArticleUnitInfo[];
  isAdmin: boolean;
  onCategoryCreated: () => Promise<void>;
  onCategoryDeleted: () => Promise<void>;
  onUnitCreated: () => Promise<void>;
  onUnitDeleted: () => Promise<void>;
  onSave: (article: any) => void;
  onCancel: () => void;
}

const ArticleForm: React.FC<ArticleFormProps> = ({
  article,
  categories,
  units,
  isAdmin,
  onCategoryCreated,
  onCategoryDeleted,
  onUnitCreated,
  onUnitDeleted,
  onSave,
  onCancel,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const availableCategories = categories.filter(
    (category) => category.key !== ArticleCategory.BUNDLE && category.isActive
  );
  const availableUnits = units.filter((unit) => unit.isActive);

  const [formData, setFormData] = useState({
    name: article?.name || "",
    description: article?.description || "",
    category: article?.category || availableCategories[0]?.key || ArticleCategory.TOOL,
    price: article?.price?.toString() || "",
    unit: article?.unit || "Stück",
    sku: article?.sku || "",
    notes: article?.notes || "",
    isPublic: article?.isPublic || false,
    isActive: article?.isActive !== undefined ? article.isActive : true,
  });

  const [customUnit, setCustomUnit] = useState("");
  const [showCustomUnit, setShowCustomUnit] = useState(false);
  const [taxonomyDialogOpen, setTaxonomyDialogOpen] = useState(false);

  // Prüfe beim Laden, ob eine custom unit verwendet wird
  React.useEffect(() => {
    if (article?.unit && !units.some((unit) => unit.isActive && unit.label === article.unit)) {
      setCustomUnit(article.unit);
      setShowCustomUnit(true);
      setFormData((prev) => ({ ...prev, unit: "custom" }));
    }
  }, [article, units]);

  const handleUnitChange = (value: string) => {
    if (value === "custom") {
      setShowCustomUnit(true);
      setFormData({ ...formData, unit: customUnit || "" });
    } else {
      setShowCustomUnit(false);
      setFormData({ ...formData, unit: value });
    }
  };

  const handleCustomUnitChange = (value: string) => {
    setCustomUnit(value);
    setFormData({ ...formData, unit: value });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const articleData = {
      ...formData,
      price: parseFloat(formData.price) || 0,
      basePrice: parseFloat(formData.price) || 0,
      currency: "EUR",
    };

    onSave(articleData);
  };

  const handleCategoryCreated = async (category: ArticleCategoryInfo) => {
    await onCategoryCreated();
    setFormData((prev) => ({ ...prev, category: category.key }));
  };

  const handleCategoryDeleted = async (deletedKey: string) => {
    await onCategoryDeleted();
    if (formData.category === deletedKey) {
      const fallbackCategory = availableCategories.find((item) => item.key !== deletedKey)?.key || "";
      setFormData((prev) => ({ ...prev, category: fallbackCategory }));
    }
  };

  const handleUnitCreated = async (unit: ArticleUnitInfo) => {
    await onUnitCreated();
    setShowCustomUnit(false);
    setFormData((prev) => ({ ...prev, unit: unit.label }));
  };

  const handleUnitDeleted = async (deletedUnit: ArticleUnitInfo) => {
    await onUnitDeleted();
    if (formData.unit !== deletedUnit.label) return;

    const fallbackUnit = availableUnits.find((item) => item.key !== deletedUnit.key);
    if (fallbackUnit) {
      setShowCustomUnit(false);
      setFormData((prev) => ({ ...prev, unit: fallbackUnit.label }));
    } else {
      setShowCustomUnit(true);
      setCustomUnit("");
      setFormData((prev) => ({ ...prev, unit: "custom" }));
    }
  };

  return (
    <Container
      maxWidth="xl"
      sx={{
        py: { xs: 1, md: 3 },
        px: { xs: 0.5, md: 3 },
        maxWidth: { xs: "100%", md: "xl" },
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 1.5, md: 3 },
          mb: 3,
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "white",
          borderRadius: 2,
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          spacing={2}
        >
          <Box>
            <Typography
              variant={isMobile ? "h5" : "h4"}
              component="h1"
              gutterBottom
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                fontWeight: "bold",
                mb: 1,
              }}
            >
              <InventoryIcon fontSize={isMobile ? "medium" : "large"} />
              {article ? "Artikel bearbeiten" : "Neuen Artikel anlegen"}
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              {article
                ? "Bearbeiten Sie die Artikeldetails"
                : "Erstellen Sie einen neuen Artikel"}
            </Typography>
          </Box>
        </Stack>
      </Paper>

      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          {/* Basic Information */}
          <Grid item xs={12}>
            <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 2 }}>
              <Typography
                variant="h6"
                sx={{ fontWeight: "bold", color: "primary.main", mb: 3 }}
              >
                Grundinformationen
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Artikelname"
                    variant="outlined"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    required
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                    <FormControl fullWidth variant="outlined">
                      <InputLabel>Kategorie</InputLabel>
                      <Select
                        value={formData.category}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            category: e.target.value,
                          })
                        }
                        label="Kategorie"
                        required
                        sx={{
                          borderRadius: 2,
                        }}
                      >
                        {availableCategories.map((category) => (
                          <MenuItem key={category.key} value={category.key}>
                            {category.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {isAdmin && (
                      <Button
                        variant="outlined"
                        startIcon={<SettingsIcon />}
                        onClick={() => setTaxonomyDialogOpen(true)}
                        sx={{ minWidth: { sm: 145 } }}
                      >
                        Verwalten
                      </Button>
                    )}
                  </Stack>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Beschreibung"
                    variant="outlined"
                    multiline
                    rows={3}
                    value={formData.description}
                    onChange={(e) =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Pricing and Units */}
          <Grid item xs={12}>
            <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 2 }}>
              <Typography
                variant="h6"
                sx={{ fontWeight: "bold", color: "primary.main", mb: 3 }}
              >
                Preis & Einheit
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Preis (€)"
                    variant="outlined"
                    type="number"
                    value={formData.price}
                    onChange={(e) =>
                      setFormData({ ...formData, price: e.target.value })
                    }
                    required
                    inputProps={{
                      step: "0.01",
                      min: "0",
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EuroIcon />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                    <FormControl fullWidth variant="outlined">
                      <InputLabel>Einheit</InputLabel>
                      <Select
                        value={showCustomUnit ? "custom" : formData.unit}
                        onChange={(e) => handleUnitChange(e.target.value)}
                        label="Einheit"
                        sx={{
                          borderRadius: 2,
                        }}
                      >
                        {availableUnits.map((unit) => (
                          <MenuItem key={unit.key} value={unit.label}>
                            {unit.label}
                          </MenuItem>
                        ))}
                        <MenuItem value="custom">Andere (eigene Angabe)</MenuItem>
                      </Select>
                    </FormControl>
                    {isAdmin && (
                      <Button
                        variant="outlined"
                        startIcon={<SettingsIcon />}
                        onClick={() => setTaxonomyDialogOpen(true)}
                        sx={{ minWidth: { sm: 145 } }}
                      >
                        Verwalten
                      </Button>
                    )}
                  </Stack>
                  {showCustomUnit && (
                    <TextField
                      fullWidth
                      label="Eigene Einheit"
                      variant="outlined"
                      value={customUnit}
                      onChange={(e) => handleCustomUnitChange(e.target.value)}
                      placeholder="Eigene Einheit eingeben..."
                      sx={{
                        mt: 2,
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                        },
                      }}
                    />
                  )}
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="SKU/Artikelnummer"
                    variant="outlined"
                    value={formData.sku}
                    onChange={(e) =>
                      setFormData({ ...formData, sku: e.target.value })
                    }
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <TagIcon />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Additional Information */}
          <Grid item xs={12}>
            <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 2 }}>
              <Typography
                variant="h6"
                sx={{ fontWeight: "bold", color: "primary.main", mb: 3 }}
              >
                Zusätzliche Informationen
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Notizen"
                    variant="outlined"
                    multiline
                    rows={2}
                    value={formData.notes}
                    onChange={(e) =>
                      setFormData({ ...formData, notes: e.target.value })
                    }
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl component="fieldset">
                    <Typography
                      variant="body1"
                      sx={{ fontWeight: "bold", mb: 1 }}
                    >
                      Sichtbarkeit & Status
                    </Typography>
                    <Stack spacing={1}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.isPublic}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                isPublic: e.target.checked,
                              })
                            }
                            color="primary"
                          />
                        }
                        label={
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <PublicIcon sx={{ mr: 1, color: "primary.main" }} />
                            <Typography variant="body2">
                              Öffentlich sichtbar
                            </Typography>
                          </Box>
                        }
                      />

                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.isActive}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                isActive: e.target.checked,
                              })
                            }
                            color="success"
                          />
                        }
                        label={
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <CheckCircleIcon
                              sx={{ mr: 1, color: "success.main" }}
                            />
                            <Typography variant="body2">Aktiv</Typography>
                          </Box>
                        }
                      />
                    </Stack>
                  </FormControl>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {isAdmin && (
            <Grid item xs={12}>
              <ArticleTaxonomyDialog
                open={taxonomyDialogOpen}
                categories={categories}
                units={units}
                onClose={() => setTaxonomyDialogOpen(false)}
                onCategoryCreated={handleCategoryCreated}
                onCategoryDeleted={handleCategoryDeleted}
                onUnitCreated={handleUnitCreated}
                onUnitDeleted={handleUnitDeleted}
              />
            </Grid>
          )}

          {/* Form Actions */}
          <Grid item xs={12}>
            <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 2 }}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                justifyContent="flex-end"
              >
                <Button
                  type="button"
                  variant="outlined"
                  size="large"
                  onClick={onCancel}
                  sx={{
                    borderRadius: 2,
                    px: 4,
                    py: 1.5,
                  }}
                >
                  Abbrechen
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  sx={{
                    borderRadius: 2,
                    px: 4,
                    py: 1.5,
                    fontWeight: "bold",
                  }}
                >
                  {article ? "Speichern" : "Erstellen"}
                </Button>
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      </form>
    </Container>
  );
};

export default ArticlesPage;
